import { Button, Card, Textarea, Toggle, useToast } from "@design";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { errorMessage, getFlowRunQueryKey, resumeFlowRunMutation } from "@/api";
import { Text } from "@/components/Text";
import { useRefreshRunViews } from "./useRefreshRunViews";

/**
 * A run paused for human review: continue (optionally with an edited output for the next step) or stop it.
 * Continuing runs the remaining steps in the background; the page polls the run while it's running.
 */
export function FlowRunReview({
  flowUuid,
  runUuid,
  output,
}: Readonly<{ flowUuid: string; runUuid: string; output: string }>) {
  const { t } = useTranslation("workflows");
  const toast = useToast();
  const queryClient = useQueryClient();
  const refreshRunViews = useRefreshRunViews();
  const [editing, setEditing] = useState(false);
  const [edited, setEdited] = useState(output);

  const resume = useMutation({
    ...resumeFlowRunMutation(),
    onSuccess: (res, vars) => {
      queryClient.setQueryData(getFlowRunQueryKey({ path: { flow_uuid: flowUuid, run_uuid: runUuid } }), res);
      void refreshRunViews(flowUuid);
      toast.success(vars.body.approval ? t("review.approved") : t("review.rejected"));
    },
    onError: (e) => toast.error(errorMessage(e, t("review.failed"))),
  });
  const send = (approval: boolean) =>
    resume.mutate({
      path: { flow_uuid: flowUuid, run_uuid: runUuid },
      body: { approval, modifiedOutput: approval && editing && edited !== output ? edited : null },
    });

  return (
    <Card>
      <section className="flex flex-col gap-3" aria-label={t("review.title")}>
        <div className="flex flex-col gap-1">
          <Text variant="body">{t("review.title")}</Text>
          <Text variant="body-sm" tone="secondary">
            {t("review.body")}
          </Text>
        </div>
        <Toggle checked={editing} onChange={setEditing} label={t("review.edit")} />
        {editing && (
          <Textarea
            aria-label={t("review.output")}
            rows={8}
            value={edited}
            onChange={(e) => setEdited(e.target.value)}
          />
        )}
        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="secondary" disabled={resume.isPending} onClick={() => send(false)}>
            {t("review.reject")}
          </Button>
          <Button variant="primary" loading={resume.isPending} onClick={() => send(true)}>
            {t("review.approve")}
          </Button>
        </div>
      </section>
    </Card>
  );
}
